import React, { useEffect, useState, useCallback } from "react";
import { PageHeader, Card, fmt } from "./UI";
import DataTable from "./DataTable";
import ReportCharts from "./ReportCharts";
import { RefreshCw, Loader2, Search, X, CheckCircle2, AlertCircle, CalendarDays } from "lucide-react";
import api from "../lib/api";

function formatError(e) {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join(", ");
  return e?.message || "Something went wrong";
}

/**
 * Reusable Field Assist report view (Transaction / Registration).
 * Props: kicker, title, subtitle, fetchPath, syncPath, columns,
 *        searchableKeys, exportFilename, testId, syncLabel
 */
export default function ReportView({
  kicker, title, subtitle, fetchPath, syncPath, statusPath, columns,
  searchableKeys, exportFilename, testId, syncLabel = "Sync now", charts,
}) {
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [mobile, setMobile] = useState("");
  const [syncResult, setSyncResult] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const qs = new URLSearchParams();
      if (startDate) qs.set("start_date", startDate);
      if (endDate) qs.set("end_date", endDate);
      if (mobile.trim()) qs.set("mobile", mobile.trim());
      const { data } = await api.get(`${fetchPath}?${qs.toString()}`);
      setRows(data.rows || []);
      setCount(data.count || 0);
    } catch (e) { setError(formatError(e)); }
    finally { setLoading(false); }
  }, [fetchPath, startDate, endDate, mobile]);

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function sync() {
    setSyncing(true); setError(""); setSyncResult(null);
    try {
      const { data } = await api.post(syncPath, {});
      // New behaviour: the sync runs in the BACKGROUND (returns immediately) so it never
      // trips the proxy/Cloudflare timeout. Poll the status endpoint until it finishes.
      if (data && (data.started || data.running) && statusPath) {
        await pollUntilDone();
      } else if (data && (data.fetched != null)) {
        setSyncResult(data); // inline (small/dry-run) response
      }
      await load();
    } catch (e) { setError(formatError(e)); }
    finally { setSyncing(false); }
  }

  async function pollUntilDone() {
    for (let i = 0; i < 200; i++) {           // up to ~10 min
      await new Promise((r) => setTimeout(r, 3000));
      try {
        const { data } = await api.get(statusPath);
        if (!data.running) {
          const lr = data.last_run || {};
          setSyncResult({ fetched: lr.fetched ?? 0, created: lr.created ?? 0, updated: lr.updated ?? 0 });
          return;
        }
        // Refresh the table periodically (~every 18s) so records appear as they sync in.
        if (i > 0 && i % 6 === 0) load();
      } catch { /* keep polling */ }
    }
  }

  function clearFilters() {
    setStartDate(""); setEndDate(""); setMobile("");
    setTimeout(load, 0);
  }

  return (
    <div className="space-y-5" data-testid={testId}>
      <PageHeader
        kicker={kicker}
        title={title}
        subtitle={subtitle}
        testId={`${testId}-header`}
        actions={
          <button onClick={sync} disabled={syncing} className="btn-teal !py-2 inline-flex items-center gap-2" data-testid={`${testId}-sync-btn`}>
            {syncing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            {syncing ? "Syncing live…" : syncLabel}
          </button>
        }
      />

      <Card>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <label className="small-caps text-slate-500 inline-flex items-center gap-1"><CalendarDays size={12} /> From date</label>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="!py-1.5 !w-auto" data-testid={`${testId}-start-date`} />
          </div>
          <div className="flex flex-col gap-1">
            <label className="small-caps text-slate-500 inline-flex items-center gap-1"><CalendarDays size={12} /> To date</label>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="!py-1.5 !w-auto" data-testid={`${testId}-end-date`} />
          </div>
          <div className="flex flex-col gap-1">
            <label className="small-caps text-slate-500">Mobile no.</label>
            <input type="text" inputMode="numeric" placeholder="e.g. 9876543210" value={mobile} onChange={(e) => setMobile(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} className="!py-1.5 !w-44" data-testid={`${testId}-mobile`} />
          </div>
          <button onClick={load} disabled={loading} className="btn-primary !py-1.5 inline-flex items-center gap-1.5" data-testid={`${testId}-apply`}>
            <Search size={13} /> Apply filters
          </button>
          <button onClick={clearFilters} className="btn-secondary !py-1.5 inline-flex items-center gap-1.5" data-testid={`${testId}-clear`}>
            <X size={13} /> Clear
          </button>
          <div className="ml-auto text-[12px] text-slate-500" data-testid={`${testId}-count`}>
            {loading ? "Loading…" : `${fmt(count)} record${count === 1 ? "" : "s"}`}
          </div>
        </div>

        {error && <div className="mt-3 text-[12px] text-rose-700 inline-flex items-center gap-1"><AlertCircle size={12} /> {error}</div>}
        {syncResult && (
          <div className="mt-3 bg-emerald-50/60 border border-emerald-200 rounded p-2.5 text-[11.5px] text-emerald-800 inline-flex items-center gap-2" data-testid={`${testId}-sync-result`}>
            <CheckCircle2 size={13} /> Live sync complete · fetched {fmt(syncResult.fetched ?? 0)} · created {fmt(syncResult.created ?? 0)} · updated {fmt(syncResult.updated ?? 0)}
          </div>
        )}
      </Card>

      {charts && charts.length > 0 && !loading && rows.length > 0 && (
        <ReportCharts rows={rows} charts={charts} testId={testId} />
      )}

      <Card>
        <DataTable
          columns={columns}
          data={rows}
          rowKey="id"
          testIdPrefix={testId}
          searchableKeys={searchableKeys}
          exportFilename={exportFilename}
          initialSort={null}
          paginated
          pageSize={25}
        />
      </Card>
    </div>
  );
}
